import { getPincodeFromUserId } from '@/components/fakeDoor/api';
import { Input } from '@/components/fakeDoor/components/mmv/input';
import { DarkButton } from '@acko-tech/building-blocks.ui.button';

import { useEffect, useState } from 'react';
import MobileOrDesktopDialog from '@/components/common/mobileOrDesktopDialog';
import { VIEW_PAGE_ADSC_ENTER_PINCODE } from '@/analytics/events';
import { viewPageAPI } from '@/analytics/middleware';
import useOnce from '@/hooks/useOnce';
import { useSelector } from 'react-redux';
import useQueryParam from '@/hooks/useQueryParam';
import { getUserIdFromCookie } from '@/utils/common';
import { LOCAL_STORAGE_KEYS } from '@/constants/storageKeys';

interface Props {
    ctaHandler: (pincode: string, currentGarageId?: string) => void;
    showModal: boolean;
    closeModal?: () => void;
    type?: string;
    subText?: string;
    prefilledPincode?: string;
}

interface Garage {
    garageId: string;
    garageName: string;
    garageAddress: string;
    garageRating: number;
    pincode: string;
}

const PINCODE_LENGTH = 6;
const formatPincodeInput = (value: string) =>
    value.replace(/\D/g, '').slice(0, PINCODE_LENGTH);

function PincodeModal({
    ctaHandler,
    showModal,
    closeModal,
    type = 'new',
    subText,
    prefilledPincode,
}: Props) {
    const queryPincode = useQueryParam('pincode');
    const [pincode, setPincode] = useState('');
    const [title, setTitle] = useState('Enter your PIN code');
    const [isManuallyEdited, setIsManuallyEdited] = useState(false);
    const [isPincodeTouched, setIsPincodeTouched] = useState(false);

    const [selectedGarageIndex, setSelectedGarageIndex] = useState<
        string | null
    >(null);
    const selectedGarageData = useSelector((state: any) => {
        return state.currentGarageData?.selectedGarageData;
    });
    const changeHandler = (val: string) => {
        setIsManuallyEdited(true);
        setPincode(formatPincodeInput(val));
    };

    const handlePincodeBlur = () => {
        setIsPincodeTouched(true);
    };

    const defaultSubText =
        'This helps us show you the nearest ACKO Drive Service Centre and unlock discounts for you';

    useOnce(() => {
        viewPageAPI({
            event_name: VIEW_PAGE_ADSC_ENTER_PINCODE,
            customAttributes: {},
            isFakeDoor: true,
        });
    });

    useEffect(() => {
        const pincode = localStorage.getItem(LOCAL_STORAGE_KEYS.PINCODE);
        if (pincode) {
            setPincode(pincode);
            setTitle('Confirm your PIN code');
        }
    }, []);
    useEffect(() => {
        const getPincode = async () => {
            if (getUserIdFromCookie()) {
                try {
                    const res = await getPincodeFromUserId();
                    const pincode = res ? res.toString() : '';
                    if (pincode.length == 6) {
                        setPincode(pincode);
                        setTitle('Confirm your PIN code');
                    } else {
                        setTitle('Enter your PIN code');
                    }
                } catch {
                    setTitle('Enter your PIN code');
                }
            } else {
                setTitle('Enter your PIN code');
            }
        };
        if (prefilledPincode) {
            setPincode(prefilledPincode);
        } else if (type === 'new' && !queryPincode) {
            getPincode();
        } else {
            const pincode =
                queryPincode || localStorage.getItem('pincode') || '';
            if (pincode) {
                setPincode(pincode);
            }
        }
    }, [prefilledPincode]);

    const handleSelect = (garageId: string, pincode: string) => {
        setSelectedGarageIndex(garageId);
        setPincode(pincode);
        setIsManuallyEdited(false);
        setIsPincodeTouched(false);
    };

    const isSelected = (id: string) => {
        return id === selectedGarageIndex;
    };

    useEffect(() => {
        const currentGarageId = localStorage.getItem('garage_id');
        if (currentGarageId) {
            setSelectedGarageIndex(currentGarageId);
        }
    }, []);

    const pincodeValue = (
        isManuallyEdited ? pincode : prefilledPincode || pincode
    ).toString();
    const formattedPincodeValue = formatPincodeInput(pincodeValue);
    const isPincodeValid = /^\d{6}$/.test(formattedPincodeValue);
    const showPincodeError = !isPincodeValid && isPincodeTouched;

    return (
        <>
            <MobileOrDesktopDialog
                hideClose={type === 'new'}
                show={showModal}
                onClose={closeModal}
                title={type === 'edit' ? 'Change your location' : title}
            >
                <div className="text">{subText ? subText : defaultSubText}</div>
                <div onBlur={handlePincodeBlur}>
                    <Input
                        type="number"
                        name="pincode"
                        label="PIN code"
                        value={formattedPincodeValue}
                        key={selectedGarageIndex}
                        handleInputs={(
                            e: React.ChangeEvent<HTMLInputElement>
                        ) => {
                            changeHandler(e.target.value);
                        }}
                        errors={showPincodeError}
                        errorLabel="Please enter a valid 6 digit pincode"
                    />
                </div>

                <div className="cta-wrapper">
                    <DarkButton
                        disabled={!isPincodeValid}
                        onClick={() =>
                            ctaHandler(
                                formattedPincodeValue,
                                selectedGarageData?.id
                            )
                        }
                    >
                        Confirm
                    </DarkButton>
                </div>
            </MobileOrDesktopDialog>
            <style jsx>{`
                .heading {
                    color: #121212;
                    font-size: 20px;
                    font-style: normal;
                    font-weight: 600;
                    line-height: 28px;
                    margin: 12px 0;
                }
                .garage-list {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    padding: 20px 0;
                }
                .title {
                    color: #4b4b4b;

                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 24px;
                }
                .text {
                    color: #4b4b4b;
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 400;
                    line-height: 24px;
                    margin-bottom: 16px;
                }
                .cta-wrapper {
                    margin-top: 24px;
                }
            `}</style>
        </>
    );
}

export default PincodeModal;
